import { ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { AppService, DCO, GetMemberDentalPlansRequest, MemberCCOEnrollment, MemberSearchParameters, SearchMemberRequest } from '../../app.service';
import { AlertService } from '../alert.service';
import { LoadingService } from '../loading.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { catchError, finalize, of, tap } from 'rxjs';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { Sort } from '@angular/material/sort';
import { MatTabGroup } from '@angular/material/tabs';

@Component({
  selector: 'app-member-search-dialog',
  templateUrl: './member-search-dialog.component.html',
  styleUrl: './member-search-dialog.component.scss'
})

export class MemberSearchDialogComponent implements OnInit {

  @ViewChild('tabGroup', { static: false }) tabGroup!: MatTabGroup;

  public initialMemberParameters: MemberSearchParameters = null;
  
  public searchMemberResults: MemberSearchParameters[] = [];
  public searchMemberResultsDataSource = new MatTableDataSource<MemberSearchParameters>(this.searchMemberResults);
  public searchMemberResultsDisplayColumns: string[] = ['DMAP', 'FirstName', 'LastName', 'DOB', 'PhoneNumber'];
  public searchMemberResultsSelectedRowDmapId: string = '';
  public selectedMember: MemberSearchParameters;

  public searchMemberPlans: MemberCCOEnrollment[] = [];
  public searchMemberPlansDataSource = new MatTableDataSource<MemberCCOEnrollment>(this.searchMemberPlans);
  public searchMemberPlansDisplayColumns: string[] = ['DCO', 'Status', 'CCOName', 'StartDate', 'EndDate'];
  public searchMemberPlansSelectedRowPlanCode: string = '';
  public selectedMemberPlan: MemberCCOEnrollment;

  public form: FormGroup;
  public dmapIdFormControl: FormControl;
  public lastFourSsnFormControl: FormControl;
  public dobFormControl: FormControl;
  public firstNameFormControl: FormControl;
  public lastNameFormControl: FormControl;
  public phoneNumberFormControl: FormControl;

  public manualForm: FormGroup;
  public manualDmapIdFormControl: FormControl;
  public manualDobFormControl: FormControl;
  public manualFirstNameFormControl: FormControl;
  public manualLastNameFormControl: FormControl;
  public manualPhoneNumberFormControl: FormControl;

  public membersActiveDCO?: MemberCCOEnrollment = null;

  public activeTabIndex = 0;

  constructor(
    private service: AppService,
    private alert: AlertService,
    private spinner: LoadingService,
    private dialog: MatDialogRef<MemberSearchDialogComponent>,
    private cdr: ChangeDetectorRef, // Inject ChangeDetectorRef
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }



  ngOnInit(): void {
    this.initialMemberParameters = this.data.searchMemberParameters;
    this.initForm();
  }

  private initForm() {
    this.form = new FormGroup({}, {
      // Attach the custom validator here.
      validators: [this.atLeastOneFieldRequired()]
    });

    this.dmapIdFormControl = new FormControl(null);
    this.form.addControl('dmapIdFormControl', this.dmapIdFormControl);

    this.lastFourSsnFormControl = new FormControl(null);
    this.form.addControl('lastFourSsnFormControl', this.lastFourSsnFormControl);

    this.dobFormControl = new FormControl(null);
    this.form.addControl('dobFormControl', this.dobFormControl);

    this.firstNameFormControl = new FormControl('', optionalMinLengthValidator(3));
    this.form.addControl('firstNameFormControl', this.firstNameFormControl);

    this.lastNameFormControl = new FormControl('', optionalMinLengthValidator(3));
    this.form.addControl('lastNameFormControl', this.lastNameFormControl);

    this.phoneNumberFormControl = new FormControl('', [Validators.pattern(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/)]);
    this.form.addControl('phoneNumberFormControl', this.phoneNumberFormControl);

    if (this.initialMemberParameters) {
      this.dmapIdFormControl.setValue(this.initialMemberParameters.dmapId);
      this.dobFormControl.setValue(new Date(this.initialMemberParameters.dob));
      this.firstNameFormControl.setValue(this.initialMemberParameters.firstName);
      this.lastNameFormControl.setValue(this.initialMemberParameters.lastName);
      this.phoneNumberFormControl.setValue(this.initialMemberParameters.phoneNumber);
      this.formatPhoneNumber();
    }

    this.manualForm = new FormGroup({}, {
      // Attach the custom validator here.
      validators: [this.atLeastOneFieldRequired()]
    });

    this.manualDmapIdFormControl = new FormControl(null);
    this.manualForm.addControl('manualDmapIdFormControl', this.manualDmapIdFormControl);

    this.manualDobFormControl = new FormControl(null);
    this.manualForm.addControl('manualDobFormControl', this.manualDobFormControl);

    this.manualFirstNameFormControl = new FormControl(null);
    this.manualForm.addControl('manualFirstNameFormControl', this.manualFirstNameFormControl);

    this.manualLastNameFormControl = new FormControl(null);
    this.manualForm.addControl('manualLastNameFormControl', this.manualLastNameFormControl);

    this.manualPhoneNumberFormControl = new FormControl('', [Validators.pattern(/^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/)]);
    this.manualForm.addControl('manualPhoneNumberFormControl', this.manualPhoneNumberFormControl);

    if (this.initialMemberParameters) {
      this.manualDmapIdFormControl.setValue(this.initialMemberParameters.dmapId);
      this.manualDobFormControl.setValue(new Date(this.initialMemberParameters.dob));
      this.manualFirstNameFormControl.setValue(this.initialMemberParameters.firstName);
      this.manualLastNameFormControl.setValue(this.initialMemberParameters.lastName);
      this.manualPhoneNumberFormControl.setValue(this.initialMemberParameters.phoneNumber);
      this.formatManualPhoneNumber();
    }
  }

  private atLeastOneFieldRequired() {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      if (!(formGroup instanceof FormGroup)) {
        return null;
      }

      // Check each control's value; if any is non-empty, we're good.
      const hasAtLeastOne = Object.values(formGroup.controls).some(control => {
        const value = control.value;
        if (value === null || value === undefined) {
          return false;
        }
        // If it's a string, trim it before checking
        return (typeof value === 'string') ? value.trim().length > 0 : true;
      });

      return hasAtLeastOne ? null : { atLeastOneFieldRequired: true };
    };
  }

  formatPhoneNumber() {
    let rawValue = this.phoneNumberFormControl.value;

    if (!rawValue) return;

    // Remove all non-digit characters
    rawValue = rawValue.replace(/\D/g, '');

    // Format: (XXX) XXX-XXXX
    if (rawValue.length > 6) {
      rawValue = `(${rawValue.substring(0, 3)}) ${rawValue.substring(3, 6)}-${rawValue.substring(6, 10)}`;
    } else if (rawValue.length > 3) {
      rawValue = `(${rawValue.substring(0, 3)}) ${rawValue.substring(3, 6)}`;
    } else if (rawValue.length > 0) {
      rawValue = `(${rawValue}`;
    }

    this.phoneNumberFormControl.setValue(rawValue, { emitEvent: false });
  }

  formatManualPhoneNumber() {
    let rawValue = this.manualPhoneNumberFormControl.value;

    if (!rawValue) return;

    // Remove all non-digit characters
    rawValue = rawValue.replace(/\D/g, '');

    // Format: (XXX) XXX-XXXX
    if (rawValue.length > 6) {
      rawValue = `(${rawValue.substring(0, 3)}) ${rawValue.substring(3, 6)}-${rawValue.substring(6, 10)}`;
    } else if (rawValue.length > 3) {
      rawValue = `(${rawValue.substring(0, 3)}) ${rawValue.substring(3, 6)}`;
    } else if (rawValue.length > 0) {
      rawValue = `(${rawValue}`;
    }

    this.manualPhoneNumberFormControl.setValue(rawValue, { emitEvent: false });
  }

  private getRawPhoneNumber(value: string): string {
    const formattedValue = value;
    return formattedValue ? formattedValue.replace(/\D/g, '') : '';
  }
  

  public onSearchSubmit() {
    if (this.form.invalid) {

      Object.keys(this.form.controls)
        .filter(key => this.form.controls[key].invalid);
      this.alert.warning('One or more form values are missing or invalid.');
      return;
    }

    
    this.selectedMember = null;
    this.selectedMemberPlan = null;
    this.searchMemberPlans = [];
    this.searchMemberPlansDataSource.data = [];


    const request = new SearchMemberRequest();

    const searchMemberParameters = new MemberSearchParameters();
    searchMemberParameters.memId = '';
    searchMemberParameters.dmapId = this.dmapIdFormControl.value || '';
    searchMemberParameters.dob = this.dobFormControl.value || null;
    searchMemberParameters.ssn = this.lastFourSsnFormControl.value || null;
    searchMemberParameters.firstName = this.firstNameFormControl.value || null;
    searchMemberParameters.lastName = this.lastNameFormControl.value || null;
    searchMemberParameters.phoneNumber = this.getRawPhoneNumber(this.phoneNumberFormControl.value);

    request.searchMemberParameters = searchMemberParameters;

    this.spinner.show();
    this.service.SearchMember(request).subscribe(
      response => {

        console.log(response);
        this.searchMemberResults = response.searchMemberResults;
        this.searchMemberResultsDataSource.data = this.searchMemberResults;

        if (this.searchMemberResults.length === 0) {
          this.spinner.hide();
          this.alert.warning('No members found.');
          return;
        }

        if (this.searchMemberResults.length == 1) {
          this.spinner.hide();
          this.loadMemberPlans(this.searchMemberResults[0]);
        } else {
          this.spinner.hide();
        }

      },
      error => {
        this.spinner.hide();
        this.alert.errorWithDetails(error, 'Error searching for members.');
      });
   }

  public loadMemberPlans(Member: MemberSearchParameters) {

    this.selectedMember = Member;
    this.selectedMemberPlan = null;

    const request = new GetMemberDentalPlansRequest();
    request.memberId = Member.dmapId;

    this.spinner.show();
    this.service.GetMemberDentalPlans(request).subscribe(
      response => {
        this.searchMemberPlans = response.memberDentalPlans;
        this.searchMemberPlansDataSource.data = this.searchMemberPlans;

        if (this.searchMemberPlans.length == 0) {
          this.alert.warning('No assigned DCOs found for member.');
        }

        if (this.searchMemberPlans.length == 1) {
          this.setMemberPlan(this.searchMemberPlans[0]);
        }

        this.membersActiveDCO = this.searchMemberPlans.find(d => d.enrollStatus === 'A');
        
        this.spinner.hide();
      },
      error => {
        this.spinner.hide();
        this.alert.errorWithDetails(error, 'Error searching for member plans.');
      });
  }

  public setMemberPlan(memberEnrollment: MemberCCOEnrollment) {
    this.selectedMemberPlan = memberEnrollment;
  }

  public onCancelClick() {
    this.dialog.close(null);
  }

  public onMemberSelect() {

    if (this.activeTabIndex == 0) {
      if (window.confirm('Are you sure you want to update this referral with the selected member?')) {
        //this.spinner.show();
        this.dialog.close({ selectedMember: this.selectedMember, sendToDCO: false, manualUpdate: false });
      }
    } else if (this.activeTabIndex == 1) {
      if (this.manualForm.valid) {
        if (window.confirm('Are you sure you want to update this referral with the entered member?')) {
          //this.spinner.show();

          const manuallyEnteredMember = new MemberSearchParameters();

          const dmapVal = this.manualDmapIdFormControl.value?.trim();
          if (dmapVal) manuallyEnteredMember.dmapId = dmapVal;

          const firstNameVal = this.manualFirstNameFormControl.value?.trim();
          if (firstNameVal) manuallyEnteredMember.firstName = firstNameVal;

          const lastNameVal = this.manualLastNameFormControl.value?.trim();
          if (lastNameVal) manuallyEnteredMember.lastName = lastNameVal;

          const dobVal = this.manualDobFormControl.value;
          if (dobVal) manuallyEnteredMember.dob = dobVal;

          const phoneVal = this.getRawPhoneNumber(this.manualPhoneNumberFormControl.value);
          if (phoneVal) manuallyEnteredMember.phoneNumber = phoneVal;
          
          this.dialog.close({ selectedMember: manuallyEnteredMember, sendToDCO: false, manualUpdate: true });
        }
      }

    }


  }

  public onMemberSelectAndSend() {
    if (window.confirm(`Are you sure you want to update this referral with the selected member and send it to their DCO (${this.membersActiveDCO?.rae})?`)) {
      //this.spinner.show();
      this.dialog.close({ selectedMember: this.selectedMember, sendToDCO: true, manualUpdate: false });
    }
  }

  public clearAllFields(): void {
    this.form.reset({
      dmapIdFormControl: null,
      lastFourSsnFormControl: null,
      dobFormControl: null,
      firstNameFormControl: null,
      lastNameFormControl: null,
      phoneNumberFormControl: ''
    });
    this.selectedMember = null;
    this.selectedMemberPlan = null;
    this.searchMemberPlans = [];
    this.searchMemberResults = [];
    this.searchMemberPlansDataSource.data = [];
    this.searchMemberResultsDataSource.data = [];
    this.membersActiveDCO = null;


    this.manualForm.reset({
      manualDmapIdFormControl: null,      
      manualDobFormControl: null,
      manualFirstNameFormControl: null,
      manualLastNameFormControl: null,
      manualPhoneNumberFormControl: ''
    });

  }

  public onMemberGridSortChange(sortState: Sort) {
    // If there's no active sort or direction is empty, reset data
    if (!sortState.active || sortState.direction === '') {
      this.searchMemberResultsDataSource.data = this.searchMemberResults;
      return;
    }

    this.searchMemberResultsDataSource.data = this.searchMemberResults.slice().sort((a, b) => {
      const isAsc = sortState.direction === 'asc';
      switch (sortState.active) {
        case 'DMAP':
          return this.compare(a.dmapId, b.dmapId, isAsc);
        case 'FirstName':
          return this.compare(a.firstName, b.firstName, isAsc);
        case 'LastName':
          return this.compare(a.lastName, b.lastName, isAsc);
        case 'DOB':
          return this.compare(new Date(a.dob), new Date(b.dob), isAsc);
        case 'PhoneNumber':
          return this.compare(a.phoneNumber, b.phoneNumber, isAsc);
        default:
          return 0;
      }
    });
  }

  public onPlansGridSortChange(sortState: Sort) {
    // If there's no active sort or direction is empty, reset data
    if (!sortState.active || sortState.direction === '') {
      this.searchMemberPlansDataSource.data = this.searchMemberPlans;
      return;
    }

    this.searchMemberPlansDataSource.data = this.searchMemberPlans.slice().sort((a, b) => {
      const isAsc = sortState.direction === 'asc';
      switch (sortState.active) {
        case 'DCO':
          return this.compare(a.rae, b.rae, isAsc);
        case 'Status':
          return this.compare(a.enrollStatus, b.enrollStatus, isAsc);
        case 'CCOName':
          return this.compare(a.cco, b.cco, isAsc);
        case 'StartDate':
          return this.compare(new Date(a.effectiveDate), new Date(b.effectiveDate), isAsc);
        case 'EndDate':
          return this.compare(new Date(a.terminationDate), new Date(b.terminationDate), isAsc);
        default:
          return 0;
      }
    });
  }

  // Helper function to handle ascending/descending
  private compare(a: string | number | Date, b: string | number | Date, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }
}

export function optionalMinLengthValidator(min: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    // If the user left it blank (null or empty string), allow it:
    if (!value) {
      return null;
    }
    // If they typed something but it's shorter than `min`, error out:
    if (value.length < min) {
      return { optionalMinLength: true };
    }
    return null;
  };
}
